/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { requestBaseURL, requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { type AxiosInstance } from 'axios';
import { createQuery } from '@/utils/createReactQuery';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useLocale } from '@/hooks/useLocale';
import { type CategoryByModelData, type CategoryByModelResponse } from '../interface';
import { type GetShopByMakeUsingGetParamsModel } from '@/api/types/browse-search-types';
import type {
  GetShopByModelParamsModel,
  ShopByModelsResponseModel,
} from '@/api/types/product-navigation-search-types';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { type QueryClient } from '@tanstack/react-query';
import type { Locale } from '@/types/i18n';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import {
  getExperimentId,
  getSearchExperimentConfig,
  buildSearchExperimentHeaders,
  buildSearchExperimentParams,
} from '@/utils/searchExperiment';
import { useVertexPersonalizationParams } from '@/features/search/hooks/useVertexPersonalizationParams';
import { getSHA256 } from '@/utils/getSHA256';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURL}/external/product-discovery/browse-search/v1/category-models`;

const ACES_URL = `${requestBaseURLGCPExtOrInt}/sls/pd/product-browse-search/v1/shop-by-page-model-list`;

type Options = {
  seoUrl: string;
  locale: Locale;
  preview: boolean;
  shouldCallACES: boolean;
  acesOptions: {
    makeId: number | null | undefined;
    isPartsPage?: boolean;
    taxonomyPath?: string;
    categoryIdPath?: string;
    partGroupId?: string;
  };
  experimentId?: string;
  visitorId?: string;
  userId?: string;
};

type OptionsQuery = Omit<Options, 'locale' | 'preview' | 'shouldCallACES'> & {
  enabled?: boolean;
};

const getCategoryModels = async (options: Options, axiosInstance?: AxiosInstance) => {
  const { shouldCallACES, locale, preview, seoUrl, acesOptions, experimentId, visitorId, userId } =
    options;
  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';
  const salesChannel = 'ECOMM';

  if (shouldCallACES) {
    const { taxonomyPath, categoryIdPath, partGroupId, makeId } = acesOptions;
    const params: GetShopByModelParamsModel = {
      country,
      customerType,
      salesChannel,
      preview,
      taxonomyPath,
      categoryIdPath,
      partGroupId,
      makeId: makeId!,
    };

    const normalizedExperimentId = getExperimentId(experimentId, true);
    const searchExpConfig = getSearchExperimentConfig({
      experimentId: normalizedExperimentId,
      experimentEnabled: true,
    });

    const hashedUserId = userId ? await getSHA256(userId) : '';
    const enhancedParams = buildSearchExperimentParams(searchExpConfig, params, {
      visitorId,
      userId: hashedUserId,
    });
    const headers = buildSearchExperimentHeaders(searchExpConfig);

    const response = await getAxios(axiosInstance).get<ShopByModelsResponseModel>(ACES_URL, {
      params: enhancedParams,
      ...(headers && { headers }),
      withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
    });

    return acesSelector(response.data);
  }

  const params: GetShopByMakeUsingGetParamsModel = {
    country,
    customerType,
    salesChannel,
    preview,
    seoUrl,
  };
  const response = await getAxios(axiosInstance).get<CategoryByModelResponse>(URL, {
    params,
  });
  return selector(response.data);
};

const selector = (data: CategoryByModelResponse): CategoryByModelData => {
  const response = data.modelRanges.map((item) => ({
    name: item.linkDisplayText,
    seoUrl: item.seoUrl,
    makeModelYearPath: item.makeModelYearPath,
  }));

  return {
    shopByMMYList: response,
    title: data.title,
    canonicalPath: data.canonicalPath,
    taxonomyPath: data.taxonomyPath,
    pageType: data.pageType,
  };
};

const acesSelector = (data: ShopByModelsResponseModel): CategoryByModelData => {
  const { title, canonicalPath, taxonomyPath, pageType, modelRanges } = data;

  return {
    title,
    canonicalPath,
    taxonomyPath,
    pageType,
    shopByMMYList: (modelRanges ?? []).map(({ linkDisplayText, makeModelYearPath, seoUrl }) => ({
      makeModelYearPath,
      name: linkDisplayText,
      seoUrl,
      taxonomyPath: undefined,
    })),
  };
};

const { useData: useCategoryByModelData, prefetch: prefetchCategoryByModelData } = createQuery<
  CategoryByModelData,
  Options
>('categoryModels', getCategoryModels);

export const useCategoryByModel = ({ enabled = true, ...rest }: OptionsQuery) => {
  const locale = useLocale();
  const { acesEnabled } = useAcesFlags();
  const experimentId = useExperimentId();
  const { visitorId, userId } = useVertexPersonalizationParams();

  const shouldCallACES = acesEnabled;

  const acesExtraConditions = shouldCallACES
    ? Boolean(
        rest?.acesOptions?.makeId &&
        (rest.acesOptions.isPartsPage ||
          rest.acesOptions.taxonomyPath ||
          rest.acesOptions.categoryIdPath ||
          rest.acesOptions.partGroupId),
      )
    : true;

  const options: Options = {
    ...rest,
    locale,
    preview: showXMPreviewDate(),
    shouldCallACES,
    experimentId,
    visitorId,
    userId,
  };

  return useCategoryByModelData({
    enabled: enabled && acesExtraConditions,
    ...options,
  });
};

export const prefetchCategoryByModel = async (
  options: Options,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
) => {
  await prefetchCategoryByModelData(queryClient, options, axiosInstance);
};
